import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import NoEjecutadoModal from './NoEjecutadoModal';
import './CUPLTable.css'; // Reutilizar estilos de tablas existentes

const normalizeText = (str) => {
  if (!str) return '';
  return str
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
};

const getUniqueOptions = (list, keyExtractor) => {
  const map = new Map();
  (list || []).forEach(item => {
    const rawVal = keyExtractor(item);
    if (!rawVal) return;
    const normKey = normalizeText(rawVal);
    if (!map.has(normKey)) {
      map.set(normKey, rawVal.trim());
    }
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
};

const DashboardTramitesModal = ({ isOpen, onClose, filterType, filterTitle, onUpdate }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tramites, setTramites] = useState([]);
  const [error, setError] = useState('');
  const [selectedTransito, setSelectedTransito] = useState('');
  const [selectedConcesionario, setSelectedConcesionario] = useState('');
  const [selectedPuntoVenta, setSelectedPuntoVenta] = useState('');
  const [noEjecTramite, setNoEjecTramite] = useState(null);

  const isSuperadmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';
  const isConcesionario = !!user?.concesionario_id || user?.role === 'concesionario' || user?.profile_name === 'admin_concesionario';
  const canFinalize = isSuperadmin || !!user?.transito_id;

  const fetchTramites = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/tramites?filterType=${filterType}`);
      // Ahora response.data tiene estructura: { data: [...], pagination: {...} }
      const tramitesArray = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setTramites(tramitesArray);
    } catch (err) {
      console.error(err);
      setError('Error al obtener la lista de trámites.');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    if (isOpen && filterType) {
      setSelectedTransito('');
      setSelectedConcesionario('');
      setSelectedPuntoVenta('');
      fetchTramites();
    }
  }, [isOpen, filterType, fetchTramites]);

  const handleOpenDetalle = (tramiteId) => {
    onClose();
    const targetPath = (user?.concesionario_id || user?.role === 'concesionario') ? '/mis-tramites' : '/bandeja-transito';
    navigate(`${targetPath}?openTramiteId=${tramiteId}`, { state: { openTramiteId: tramiteId } });
  };

  const handleFinalizar = async (tramiteId) => {
    if (window.confirm('¿Está seguro de finalizar y ejecutar este trámite? Esta acción es definitiva.')) {
      try {
        setLoading(true);
        await axios.put(`/api/tramites/${tramiteId}/estado`, {
          estado: 'tramite_ejecutado',
          observaciones: ''
        });
        alert('✅ Trámite finalizado exitosamente');
        fetchTramites();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error(err);
        alert('❌ Error al actualizar el trámite');
        setLoading(false);
      }
    }
  };

  const handleHabilitarReintento = async (tramiteId) => {
    if (!window.confirm('¿Habilitar el reintento de ejecución? El trámite volverá a "Listos para Finalizar" si cumple los requisitos.')) return;
    try {
      const res = await axios.post(`/api/tramites/${tramiteId}/habilitar-reintento`);
      alert('✅ ' + (res.data?.message || 'Reintento habilitado'));
      fetchTramites();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Error al habilitar reintento'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'radicado': return '📝 Radicado';
      case 'pre_asignado': return '🚗 Pre-asignado';
      case 'novedad_pendiente_soat': return '📄 Pendiente SOAT';
      case 'novedad_soat_cargado': return '🛡️ SOAT Cargado';
      case 'novedad_soat_vigente': return '🛡️ SOAT Vigente';
      case 'novedad_pendiente_cupl': return '💳 Pendiente CUPL';
      case 'novedad_cupl_generado': return '📄 CUPL Generado';
      case 'novedad_cupl_pagado': return '💳 CUPL Pagado';
      case 'tramite_ejecutado': return '🎉 Ejecutado';
      case 'rechazado': return '❌ Rechazado';
      default: return estado;
    }
  };

  const transitoOptions = getUniqueOptions(tramites, t => t.transito_name);

  const concesionarioOptions = getUniqueOptions(tramites, t => t.creador_concesionario_name || 'Independiente');

  const puntoVentaOptions = getUniqueOptions(tramites, t => t.punto_venta_name || 'Sin Punto de Venta');

  const filteredTramites = tramites.filter(t => {
    if (isSuperadmin && selectedTransito && normalizeText(t.transito_name) !== normalizeText(selectedTransito)) return false;
    const conc = t.creador_concesionario_name || 'Independiente';
    if (isSuperadmin && selectedConcesionario && normalizeText(conc) !== normalizeText(selectedConcesionario)) return false;
    const pv = t.punto_venta_name || 'Sin Punto de Venta';
    if (selectedPuntoVenta && normalizeText(pv) !== normalizeText(selectedPuntoVenta)) return false;
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1050 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '950px', width: '95%' }}>
        <div className="modal-header">
          <h3>📋 Trámites: {filterTitle}</h3>
          <button className="close-btn" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
        </div>
        
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
          {error && <div className="error-banner" style={{ marginBottom: '15px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '10px', borderRadius: '6px', border: '1px solid #fee2e2' }}>{error}</div>}

          {!loading && tramites.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '15px',
              marginBottom: '15px',
              background: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {isSuperadmin && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>🏛️ Tránsito:</label>
                    <select
                      value={selectedTransito}
                      onChange={e => setSelectedTransito(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="">Todos los tránsitos ({tramites.length})</option>
                      {transitoOptions.map(t => {
                        const count = tramites.filter(tr => normalizeText(tr.transito_name) === normalizeText(t)).length;
                        return (
                          <option key={t} value={t}>
                            {t} ({count})
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>🏢 Concesionario:</label>
                    <select
                      value={selectedConcesionario}
                      onChange={e => setSelectedConcesionario(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="">Todos los concesionarios</option>
                      {concesionarioOptions.map(c => {
                        const count = tramites.filter(tr => normalizeText(tr.creador_concesionario_name || 'Independiente') === normalizeText(c)).length;
                        return (
                          <option key={c} value={c}>
                            {c} ({count})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}

              {isConcesionario && puntoVentaOptions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>🏪 Punto de Venta:</label>
                  <select
                    value={selectedPuntoVenta}
                    onChange={e => setSelectedPuntoVenta(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#334155',
                      background: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="">Todos los Puntos de Venta</option>
                    {puntoVentaOptions.map(pv => {
                      const count = tramites.filter(tr => normalizeText(tr.punto_venta_name || 'Sin Punto de Venta') === normalizeText(pv)).length;
                      return (
                        <option key={pv} value={pv}>
                          {pv} ({count})
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {(selectedTransito || selectedConcesionario || selectedPuntoVenta) && (
                <button
                  onClick={() => {
                    setSelectedTransito('');
                    setSelectedConcesionario('');
                    setSelectedPuntoVenta('');
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    color: '#dc2626',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ✖️ Limpiar Filtros ({filteredTramites.length} resultados)
                </button>
              )}
            </div>
          )}

          {loading ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>⏳ Cargando lista de trámites...</p>
          ) : filteredTramites.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '500' }}>
              📭 No se encontraron trámites con los filtros seleccionados.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="cupl-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>ID</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Placa</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Tránsito</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Tipo de Trámite</th>
                  {user?.role === 'superadmin' && (
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Creador</th>
                  )}
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Estado</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Fecha</th>
                  <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTramites.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 10px', fontWeight: '600', color: '#1e293b' }}>#{t.id}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <span className="plate" style={{ display: 'inline', fontSize: '12px', padding: '3px 8px', borderRadius: '4px', background: '#fef08a', color: '#854d0e', fontWeight: '700', border: '1px dashed #ca8a04' }}>
                        {t.datos_adicionales?.placa_referencia || 'SIN PLACA'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 10px', color: '#334155', fontSize: '13px' }}>{t.transito_name}</td>
                    <td style={{ padding: '12px 10px', color: '#334155', fontSize: '13px', fontWeight: '500' }}>{t.tramite_type_name}</td>
                    {user?.role === 'superadmin' && (
                      <td style={{ padding: '12px 10px', color: '#475569', fontSize: '13px' }}>
                        <strong>{t.creador_name}</strong>
                        {t.creador_concesionario_name || t.creador_transito_name ? (
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            🏢 {t.creador_concesionario_name || t.creador_transito_name}
                          </div>
                        ) : (
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            💼 Independiente
                          </div>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '12px 10px', fontSize: '13px' }}>
                      <span className={`status ${t.estado}`} style={{ display: 'inline-block', fontSize: '12px', fontWeight: '600' }}>
                        {getStatusLabel(t.estado)}
                      </span>
                      {filterType === 'novedadesPendientes' && (
                        <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {t.factura_count === 0 && t.datos_adicionales?.tipo_flujo !== 'otros_tramites' && t.estado !== 'rechazado' && t.estado !== 'tramite_ejecutado' && (
                            <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600', background: '#fef2f2', padding: '3px 6px', borderRadius: '4px', border: '1px solid #fca5a5' }}>
                              ⚠️ Sin Factura
                            </span>
                          )}
                          {t.pending_novelties_count > 0 && (
                            <span style={{ fontSize: '10px', color: '#92400e', fontWeight: '600', background: '#fffbeb', padding: '3px 6px', borderRadius: '4px', border: '1px solid #fde68a' }}>
                              🛑 {t.pending_novelty_name || 'Novedad pendiente'}
                            </span>
                          )}
                        </div>
                      )}
                      {t.intento_ejecucion && (
                        <div style={{ marginTop: '5px', fontSize: '10px', color: '#92400e', fontWeight: '600', background: '#fffbeb', padding: '4px 6px', borderRadius: '4px', border: '1px solid #fcd34d', maxWidth: '230px' }}>
                          ⛔ {t.intento_ejecucion.motivo}
                          {t.intento_ejecucion.observaciones ? ` · ${t.intento_ejecucion.observaciones}` : ''}
                          <div style={{ fontWeight: 400, color: '#a16207' }}>
                            {formatDate(t.intento_ejecucion.fecha)} · {t.intento_ejecucion.usuario}
                            {t.intento_ejecucion.total_intentos > 1 ? ` · ${t.intento_ejecucion.total_intentos} intentos` : ''}
                            {t.intento_ejecucion.bloquea_finalizar ? ' · Bloqueado' : (t.pending_novelties_count > 0 ? ' · En novedades' : ' · Listo para reintento')}
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '12px' }}>{formatDate(t.created_at)}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleOpenDetalle(t.id)}
                          className="btn-primary"
                          style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', background: '#0284c7', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                          title="Ver Detalle y Subsanar Novedades"
                        >
                          🔍 Detalle / Subsanar
                        </button>
                        {filterType === 'listosParaFinalizar' && canFinalize && (
                          <button
                            onClick={() => handleFinalizar(t.id)}
                            className="btn-primary"
                            style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                            title="Finalizar Trámite"
                          >
                            🔒 Finalizar
                          </button>
                        )}
                        {filterType === 'listosParaFinalizar' && canFinalize && user?.profile_name !== 'auxiliar transiapp' && (
                          <button
                            onClick={() => setNoEjecTramite(t)}
                            style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', background: '#d97706', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                            title="Registrar intento de ejecución no exitoso"
                          >
                            ⛔ No ejecutado
                          </button>
                        )}
                        {filterType === 'intentosEjecucion' && t.intento_ejecucion?.bloquea_finalizar && user?.profile_name !== 'auxiliar transiapp' && !['auxiliar_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && (
                          <button
                            onClick={() => handleHabilitarReintento(t.id)}
                            style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', background: '#059669', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                            title="Habilitar reintento: el trámite vuelve a Listos para Finalizar"
                          >
                            🔁 Habilitar reintento
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-secondary" style={{ padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', fontWeight: '600', color: '#475569' }}>
            Cerrar
          </button>
        </div>
      </div>
      <NoEjecutadoModal
        isOpen={!!noEjecTramite}
        tramite={noEjecTramite}
        onClose={() => setNoEjecTramite(null)}
        onSuccess={() => { fetchTramites(); if (onUpdate) onUpdate(); }}
      />
    </div>
  );
};

export default DashboardTramitesModal;
